/**
 * DuplicateWarningDialog
 * ──────────────────────
 * Feb 2026 — Phase 2 of B2 IntakeWizard extraction.
 *
 * Pure extraction of the inline dialog at App.js lines ~71773–71978.
 * No behavior change — same JSX, same actions, same SSN reveal flow.
 *
 * App.js's inline copy is NOT removed in Phase 2 (legacy must stay
 * provably untouched).  Phase 3 will replace it with this component.
 */
import React from 'react';
import { formatClientStatus } from '../../utils/displayFormat';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, Eye, EyeOff, Edit } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIDENCE_BADGE_COLORS = {
  exact: 'bg-red-600 text-white',
  strong: 'bg-orange-600 text-white',
  likely: 'bg-yellow-600 text-white',
  possible: 'bg-blue-600 text-white',
};
const CONFIDENCE_CARD_COLORS = {
  exact: 'bg-red-50 border-red-300',
  strong: 'bg-orange-50 border-orange-300',
  likely: 'bg-yellow-50 border-yellow-300',
  possible: 'bg-blue-50 border-blue-300',
};
const CONFIDENCE_REASON_COLORS = {
  exact: 'bg-red-100',
  strong: 'bg-orange-100',
};

export default function DuplicateWarningDialog({
  open,
  onOpenChange,
  duplicates = [],
  revealedSSNs = {},
  setRevealedSSNs,
  isCreating = false,
  onCancel,
  onConfirmCreate,
  onUseExisting,
  onViewChart,
}) {
  const handleOpenChange = (next) => {
    if (!next && typeof setRevealedSSNs === 'function') {
      setRevealedSSNs({});
    }
    onOpenChange?.(next);
  };

  const handleRevealSSN = async (dup) => {
    try {
      const response = await axios.get(`${API}/clients/${dup.id}/reveal-ssn`);
      if (response.data?.ssn && typeof setRevealedSSNs === 'function') {
        setRevealedSSNs((prev) => ({ ...prev, [dup.id]: response.data.ssn }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unable to reveal SSN');
    }
  };

  const handleHideSSN = (dupId) => {
    if (typeof setRevealedSSNs !== 'function') return;
    setRevealedSSNs((prev) => {
      const next = { ...prev };
      delete next[dupId];
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            Potential Duplicate Client Detected
          </DialogTitle>
          <DialogDescription>
            We found {duplicates.length} existing client{duplicates.length > 1 ? 's' : ''} with similar information. Please review carefully before proceeding.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {duplicates.map((dup) => {
            const bgClass = CONFIDENCE_CARD_COLORS[dup.confidence] || 'bg-yellow-50 border-yellow-300';
            const badgeClass = CONFIDENCE_BADGE_COLORS[dup.confidence] || 'bg-yellow-600';
            const reasonClass = CONFIDENCE_REASON_COLORS[dup.confidence] || 'bg-yellow-100';
            return (
              <div key={dup.id} className={`border rounded-lg p-4 ${bgClass}`} data-testid={`duplicate-card-${dup.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <Badge className={badgeClass}>
                        {dup.confidence_label || 'Potential Match'}
                      </Badge>
                      <h4 className="font-semibold text-lg">{dup.full_name}</h4>
                      <Badge variant="outline" className="capitalize">
                        {dup.status ? formatClientStatus(dup.status) : 'Unknown'}
                      </Badge>
                      {dup.mrn && (
                        <span className="text-sm text-gray-600">
                          {dup.mrn.startsWith('REF-') ? 'Ref' : 'MRN'}: #{dup.mrn.replace(/MRN-|REF-/, '')}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div><span className="text-gray-600">DOB:</span> {dup.date_of_birth || 'Not provided'}</div>
                      <div><span className="text-gray-600">Phone:</span> {dup.phone || 'Not provided'}</div>
                      <div><span className="text-gray-600">Email:</span> {dup.email || 'Not provided'}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">SSN:</span>
                        {dup.ssn_masked ? (
                          <>
                            <span className="font-mono">
                              {revealedSSNs[dup.id] || dup.ssn_masked}
                            </span>
                            {!revealedSSNs[dup.id] && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleRevealSSN(dup)}
                                data-testid={`reveal-ssn-${dup.id}`}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Reveal
                              </Button>
                            )}
                            {revealedSSNs[dup.id] && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleHideSSN(dup.id)}
                                data-testid={`hide-ssn-${dup.id}`}
                              >
                                <EyeOff className="h-3 w-3 mr-1" />
                                Hide
                              </Button>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">Not on file</span>
                        )}
                      </div>
                    </div>

                    <div className={`rounded p-2 ${reasonClass}`}>
                      <p className="text-sm font-medium text-gray-900">Why this matched:</p>
                      <ul className="list-disc list-inside text-sm text-gray-700">
                        {(dup.match_reasons || []).map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="ml-4 flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewChart?.(dup)}
                      data-testid={`view-chart-${dup.id}`}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View Chart
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => onUseExisting?.(dup)}
                      data-testid={`use-existing-${dup.id}`}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Use This Client
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 border-t pt-4">
          <div className="flex-1">
            <p className="text-sm text-gray-600">
              If this is truly a <strong>different person</strong> with the same details, you can create a new record.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Tip: Adding SSN helps distinguish between people with the same name and DOB.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => onCancel?.()}
            data-testid="duplicate-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirmCreate?.()}
            className="bg-yellow-600 hover:bg-yellow-700"
            disabled={isCreating}
            data-testid="duplicate-create-anyway"
          >
            {isCreating ? 'Creating...' : 'Create New Client Anyway'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
